import { Component, EventEmitter, Input, Output, OnInit } from '@angular/core';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { TranslateService } from '@ngx-translate/core';
import { ToastrService } from 'ngx-toastr';
import { mailIcon } from 'src/app/app-icons';
import { CompleteRegistrationFlowModel } from 'src/app/models/registration-flow/complete-registration-flow.model';
import { PendingRegistrationFlowModel } from 'src/app/models/registration-flow/pending-registration-flow.model';
import { RegistrationFlowAnswerModel } from 'src/app/models/registration-flow/registration-flow-answer.model';
import { RegistrationFlowPersonModel } from 'src/app/models/registration-flow/registration-flow-person.model';
import { RegistrationLinkQuestionTypeModel } from 'src/app/models/registration-link/registration-link-question-type.model';
import { RegistrationLinkQuestionModel } from 'src/app/models/registration-link/registration-link-question.model';
import { LoadingService } from 'src/app/services/loading.service';
import { RegistrationFlowService } from 'src/app/services/registration-flow.service';
import { RegistrationLinkService } from 'src/app/services/registration-link.service';

type OnboardingStep = 'questions' | 'employees' | 'complete';
type AnswerValue = string | string[] | null;

@Component({
  selector: 'app-registration-flow-onboarding',
  standalone: false,
  templateUrl: './registration-flow-onboarding.component.html',
  styleUrl: './registration-flow-onboarding.component.scss',
})
export class RegistrationFlowOnboardingComponent implements OnInit {
  @Input()
  firstName = '';

  @Input()
  pendingRegistrationFlow: PendingRegistrationFlowModel | null = null;

  @Output()
  completed = new EventEmitter<void>();

  questions: RegistrationLinkQuestionModel[] = [];
  allowAddingEmployees = false;
  createLocationAutomatically = false;
  newLocationParentId: string | null = null;

  visible = false;
  currentStep: OnboardingStep = 'questions';

  answers: Record<string, AnswerValue> = {};
  errors: Record<string, boolean> = {};

  teamMembers: RegistrationFlowPersonModel[] = [
    { firstName: '', lastName: '', email: '' }
  ];

  teamMemberErrors: Record<number, string> = {};
  isSaving = false;
  employeesSkipped = false;

  questionType = RegistrationLinkQuestionTypeModel;

  get steps() {
    return [
      ...(this.questions.length > 0
        ? [{
            id: 'questions' as const,
            translationKey: 'REGISTRATION-FLOW.ONBOARDING.STEPS.PROFILE'
          }]
        : []),
      ...(this.showTeamsStep
        ? [{
            id: 'employees' as const,
            translationKey: 'REGISTRATION-FLOW.ONBOARDING.STEPS.TEAM'
          }]
        : []),
      {
        id: 'complete' as const,
        translationKey: 'REGISTRATION-FLOW.ONBOARDING.STEPS.START'
      }
    ].map((step, index) => ({
      ...step,
      number: index + 1
    }));
  }

  get stepNumber(): number {
    const index = this.steps.findIndex(step => step.id === this.currentStep);
    return index >= 0 ? index + 1 : 1;
  }

  get showTeamsStep(): boolean {
    return this.allowAddingEmployees && !this.isInvitedEmployee;
  }

  get isInvitedEmployee(): boolean {
    return this.pendingRegistrationFlow?.isInvitedEmployee ?? false;
  }

  constructor(
    private sanitized: DomSanitizer,
    private registrationLinkService: RegistrationLinkService,
    private registrationFlowService: RegistrationFlowService,
    private loadingService: LoadingService,
    private toastService: ToastrService,
    private translateService: TranslateService
  ) {}

  ngOnInit(): void {
    if (!this.pendingRegistrationFlow) {
      return;
    }

    this.loadingService.load(
      this.registrationLinkService.getRegistrationLinkFull(
        this.pendingRegistrationFlow.registrationLinkId
      )
    ).subscribe(registrationLink => {
      this.questions = registrationLink.questions;
      this.allowAddingEmployees =
        registrationLink.registrationLink.allowAddingEmployees;
      this.createLocationAutomatically =
        registrationLink.registrationLink.createLocationAutomatically;
      this.newLocationParentId =
        registrationLink.registrationLink.parentLocationId;

      if(
        this.questions.length === 0 &&
        !this.showTeamsStep
      ) {
        this.completed.emit();
        return;
      }

      if (this.questions.length === 0) {
        this.currentStep = this.showTeamsStep ? 'employees' : 'complete';
      }

      this.visible = true;
    });
  }

  getMailIcon(): SafeHtml {
    return this.sanitized.bypassSecurityTrustHtml(mailIcon);
  }

  getTextAnswer(questionId: string): string {
    const answer = this.answers[questionId];
    return typeof answer === 'string' ? answer : '';
  }

  setAnswer(questionId: string, value: AnswerValue): void {
    this.answers[questionId] = value;
    this.errors[questionId] = false;
  }

  toggleOption(questionId: string, optionId: string, multiple: boolean): void {
    const current = this.answers[questionId];

    if (multiple) {
      const selected = Array.isArray(current) ? current : [];

      this.setAnswer(
        questionId,
        selected.includes(optionId)
          ? selected.filter(id => id !== optionId)
          : [...selected, optionId]
      );

      return;
    }

    this.setAnswer(questionId, current === optionId ? null : optionId);
  }

  isSelected(questionId: string, optionId: string): boolean {
    const answer = this.answers[questionId];

    return Array.isArray(answer)
      ? answer.includes(optionId)
      : answer === optionId;
  }

  next(): void {
    if (this.isSaving) {
      return;
    }

    if (this.currentStep === 'questions') {
      if (!this.validateAnswers()) {
        return;
      }

      this.currentStep = this.showTeamsStep ? 'employees' : 'complete';
      return;
    }

    if (this.currentStep === 'employees') {
      if (!this.validateTeamMembers()) {
        return;
      }

      this.currentStep = 'complete';
    }
  }

  back(): void {
    if (this.isSaving) {
      return;
    }

    const currentIndex = this.steps.findIndex(
      step => step.id === this.currentStep
    );

    if (currentIndex <= 0) {
      return;
    }

    this.currentStep = this.steps[currentIndex - 1].id;
  }

  skipEmployees(): void {
    if (this.isSaving) {
      return;
    }

    this.teamMemberErrors = {};
    this.employeesSkipped = true;
    this.currentStep = 'complete';
  }

  addPerson(): void {
    this.employeesSkipped = false;
    this.teamMembers.push({
      firstName: '',
      lastName: '',
      email: ''
    });
  }

  removePerson(index: number): void {
    if (this.isSaving || index < 0 || index >= this.teamMembers.length) {
      return;
    }

    this.teamMembers.splice(index, 1);

    if (this.teamMembers.length === 0) {
      this.teamMembers.push({
        firstName: '',
        lastName: '',
        email: ''
      });
    }

    delete this.teamMemberErrors[index];
  }

  finish(): void {
    if (this.isSaving || !this.pendingRegistrationFlow) {
      return;
    }

    if (!this.validateAnswers()) {
      this.currentStep = 'questions';
      return;
    }

    if (
      this.showTeamsStep &&
      !this.employeesSkipped &&
      !this.validateTeamMembers()
    ) {
      this.currentStep = 'employees';
      return;
    }

    const model: CompleteRegistrationFlowModel = {
      answers: this.buildAnswers(),
      persons: this.showTeamsStep && !this.employeesSkipped
        ? this.teamMembers
            .filter(person =>
              person.firstName.trim() ||
              person.lastName.trim() ||
              person.email.trim()
            )
            .map(person => ({
              firstName: person.firstName.trim(),
              lastName: person.lastName.trim(),
              email: person.email.trim()
            }))
        : []
    };

    this.isSaving = true;

    this.loadingService.load(
      this.registrationFlowService.completeRegistrationFlow(
        this.pendingRegistrationFlow.registrationLinkId,
        model
      )
    ).subscribe({
      next: () => {
        this.isSaving = false;
        this.visible = false;
        this.completed.emit();
      },
      error: () => {
        this.isSaving = false;
        this.toastService.error(
          this.translateService.instant(
            'COMMON.AN-ERROR-HAS-OCCURRED'
          )
        );
      }
    });
  }

  private buildAnswers(): RegistrationFlowAnswerModel[] {
    return this.questions.map(question => {
      const value = this.answers[question.id];

      switch (question.type) {
        case RegistrationLinkQuestionTypeModel.Text:
          return {
            questionId: question.id,
            answer: typeof value === 'string' ? value : null,
            selectedOptionIds: []
          };

        case RegistrationLinkQuestionTypeModel.MultipleChoice:
          return {
            questionId: question.id,
            answer: null,
            selectedOptionIds: Array.isArray(value) ? value : []
          };

        case RegistrationLinkQuestionTypeModel.SingleChoice:
        case RegistrationLinkQuestionTypeModel.YesNo:
          return {
            questionId: question.id,
            answer: null,
            selectedOptionIds: typeof value === 'string' ? [value] : []
          };

        default:
          return {
            questionId: question.id,
            answer: null,
            selectedOptionIds: []
          };
      }
    });
  }

  private validateAnswers(): boolean {
    this.errors = {};

    for (const question of this.questions) {
      if (!question.isRequired) {
        continue;
      }

      const answer = this.answers[question.id];

      const empty =
        answer == null ||
        (Array.isArray(answer) && answer.length === 0) ||
        (typeof answer === 'string' && !answer.trim());

      if (empty) {
        this.errors[question.id] = true;
      }
    }

    return Object.keys(this.errors).length === 0;
  }

  private validateTeamMembers(): boolean {
    this.teamMemberErrors = {};

    this.teamMembers.forEach((person, index) => {
      const firstName = person.firstName.trim();
      const lastName = person.lastName.trim();
      const email = person.email.trim();

      if (!firstName && !lastName && !email) {
        return;
      }

      if (!firstName || !lastName || !email) {
        this.teamMemberErrors[index] =
          'REGISTRATION-FLOW.ONBOARDING.VALIDATION.TEAM-MEMBER-REQUIRED';
        return;
      }

      if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
        this.teamMemberErrors[index] =
          'REGISTRATION-FLOW.ONBOARDING.VALIDATION.INVALID-EMAIL';
      }
    });

    return Object.keys(this.teamMemberErrors).length === 0;
  }
}